import { ChangeDetectorRef, Component, Host, Input, OnInit } from '@angular/core';
import { ControlContainer, Validators } from '@angular/forms';
import { FieldListItem } from '@assetaccountant/api/models/field-list-item';
import { FieldValueDto } from '@assetaccountant/api/models/field-value.dto';
import {
  FieldAllocationGroup,
  FieldValueAllocationGroup,
} from '@assetaccountant/assets/models/field-form-model';
import { emptyValue, TypedFormBuilder } from '@assetaccountant/core/utils/typed-forms';
import {
  validateIfNotLast,
  validateWithPrevious,
} from '@assetaccountant/core/utils/validation-utils';

// 0.5% margin
const ALLOCATION_MARGIN = 0.005;

@Component({
  selector: 'app-edit-field-allocations[formGroup], app-edit-field-allocations[formGroupName]',
  templateUrl: './edit-field-allocations.component.html',
  styleUrls: ['./edit-field-allocations.component.scss'],
  standalone: false,
})
export class EditFieldAllocationsComponent implements OnInit {
  @Input() field: FieldListItem;

  get form() {
    return this.controlContainer.control as FieldAllocationGroup;
  }

  unassignedAllocation?: number;

  private get lastAllocationGroup() {
    return this.form.controls.valueIdAllocations.length > 0
      ? this.form.controls.valueIdAllocations.controls[
          this.form.controls.valueIdAllocations.length - 1
        ]
      : null;
  }

  constructor(
    private cd: ChangeDetectorRef,
    @Host() private controlContainer: ControlContainer,
    private fb: TypedFormBuilder,
  ) {}

  ngOnInit() {
    if (!this.field) {
      throw new Error('No field provided');
    }
    if (!this.form) {
      throw new Error('No form provided');
    }

    // Initialise and subscribe to existing groups
    // Timeout allows for other components to update the form first
    setTimeout(() => {
      this.form.controls.valueIdAllocations.controls.forEach(fg => {
        this.initAllocationFormGroup(fg);
      });
      this.checkFieldValueAllocations();

      this.cd.detectChanges();
    });
  }

  remove(index: number) {
    this.form.controls.valueIdAllocations.removeAt(index);
    this.onAllocationChange();
  }

  isDisabledValue(index: number, fieldValue: FieldValueDto) {
    return this.form.controls.valueIdAllocations.value
      .filter((_, i) => i !== index)
      .some(({ valueId }) => valueId === fieldValue.id);
  }

  private initAllocationFormGroup(formGroup: FieldValueAllocationGroup) {
    formGroup.controls.valueId.setValidators(
      validateIfNotLast(this.form.controls.valueIdAllocations, Validators.required),
    );
    formGroup.controls.allocation.setValidators([
      Validators.required,
      Validators.min(0.0001),
      validateWithPrevious(this.form.controls.valueIdAllocations, (control, previousValues) =>
        Validators.max(getRemainingAllocation(previousValues) + ALLOCATION_MARGIN)(control),
      ),
    ]);
  }

  onAllocationChange() {
    // Timeout allows for the value update to propagate
    setTimeout(() => {
      this.form.controls.valueIdAllocations.controls.forEach(fg =>
        fg.controls.allocation.updateValueAndValidity(),
      );

      const length = this.form.controls.valueIdAllocations.value.length;
      const nonLastAllocations =
        length > 1 ? this.form.controls.valueIdAllocations.controls.slice(0, length - 1) : [];

      if (
        nonLastAllocations.every(fg => fg.valid) &&
        this.lastAllocationGroup &&
        !this.lastAllocationGroup.value.valueId
      ) {
        const finalAllocation = getRemainingAllocation(nonLastAllocations.map(fg => fg.value));
        if (finalAllocation <= 0) {
          this.form.controls.valueIdAllocations.removeAt(length - 1);
        } else {
          this.lastAllocationGroup.controls.allocation.setValue(finalAllocation);
        }
      }

      this.checkFieldValueAllocations();

      this.cd.detectChanges();
    });
  }

  private checkFieldValueAllocations() {
    this.unassignedAllocation = getRemainingAllocation(this.form.controls.valueIdAllocations.value);

    if (
      this.form.controls.valueIdAllocations.valid &&
      (!this.lastAllocationGroup || !!this.lastAllocationGroup.value.valueId)
    ) {
      if (this.unassignedAllocation > 0) {
        const newGroup = this.fb.group({
          valueId: emptyValue<string>(),
          allocation: this.unassignedAllocation,
        });
        this.form.controls.valueIdAllocations.push(newGroup);
        this.initAllocationFormGroup(newGroup);
      }
    }
  }
}

function getRemainingAllocation(values: Array<{ allocation: number | string | null }>) {
  const result = values.reduce((remaining, current) => +remaining - +(current.allocation || 0), 1);

  // Deal with floating point nastiness
  const fixedResult = +result.toFixed(6);

  if (fixedResult <= ALLOCATION_MARGIN) {
    return 0;
  }

  return fixedResult;
}
